import { useState, useEffect, useRef } from "react";
import { useCountUp } from "react-countup";

import { Text } from "@krea/customer-features/shared-components/text";
import { twMerge } from "@krea/shared/design-system/tailwind-utils";

interface IProgressBarProps {
  isCompleted: boolean;
}

const ProgressBar = ({ isCompleted }: IProgressBarProps) => {
  const minValue = 0;
  const maxValue = 100;
  const maxTime = 30; // seconds
  const maxCompleted = 98; // %

  const countUpRef = useRef<HTMLSpanElement>(null);

  const [value, setValue] = useState(minValue);

  const calcTime = (maxTime * maxValue) / maxCompleted;
  const step = maxValue / calcTime;

  useEffect(() => {
    let interval: number | undefined;

    if (isCompleted) {
      setValue(maxValue);
      clearInterval(interval);
    } else {
      interval = setInterval(() => {
        setValue((prevValue) =>
          Math.round(prevValue) < Math.round(step * maxTime)
            ? prevValue + step
            : prevValue,
        );
      }, 1 * 1000) as unknown as number;
    }

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompleted]);

  const { update } = useCountUp({
    ref: countUpRef,
    start: 0,
    delay: 1000,
    duration: 1,
    suffix: "%",
    end: 100,
  });

  useEffect(() => {
    update(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="tw-flex tw-items-center">
      <div className="tw-relative tw-w-full tw-overflow-hidden tw-rounded">
        <div
          className={twMerge("tw-h-2 tw-bg-primary-150", "w-full tw-rounded")}
        >
          <div
            className={twMerge(
              "tw-top-0 tw-left-0 tw-transition-all tw-duration-1000 tw-ease-linear tw-absolute tw-h-full tw-rounded tw-bg-primary",
            )}
            style={{ width: `${value}%` }}
          />
        </div>
      </div>
      <Text
        size="sm"
        className={twMerge(
          "tw-w-10 !tw-p-0 !tw-pb-1 !tw-pl-1 !tw-leading-4 shrink-0 tw-font-bold tw-text-gray-400 tw-text-right",
        )}
      >
        <span ref={countUpRef} />
      </Text>
    </div>
  );
};

export default ProgressBar;
